import { useEffect, useState } from 'react';
import { AudioPermissionState } from '../types';

interface UseAudioPermissionReturn {
  audioPermission: AudioPermissionState;
  audioStream: MediaStream | null;
  requestPermission: () => Promise<void>;
  error: string | null;
}

/**
 * Custom hook to manage user's microphone permission state and audio stream.
 *
 * - Checks mic permission status passively.
 * - Provides a method to actively request microphone access.
 * - Exposes current permission state, active MediaStream (if any), and any errors.
 *
 * Returns:
 *   {
 *     audioPermission: 'idle' | 'requesting' | 'granted' | 'denied',
 *     audioStream: MediaStream | null,
 *     requestPermission: () => Promise<void>,
 *     error: string | null,
 *   }
 */
export const useAudioPermission = (): UseAudioPermissionReturn => {
  const [audioPermission, setAudioPermission] = useState<AudioPermissionState>('idle');
  const [audioStream, setAudioStream] = useState<MediaStream | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Passive check: see if mic permission is already granted (no prompt)
  useEffect(() => {
    const checkExistingAudioPermission = async () => {
      try {
        // Prefer Permissions API when available
        const hasPermissionsApi = typeof navigator !== 'undefined' && 'permissions' in navigator;
        if (hasPermissionsApi) {
          const status = await navigator.permissions.query({ name: 'microphone' });
          if (status?.state === 'granted') {
            setAudioPermission('granted');
          }
          if (status?.state === 'denied') {
            setAudioPermission('denied');
          }
        }
      } catch {
        // Ignore passive check errors; user can still grant explicitly
      }
    };

    void checkExistingAudioPermission();
  }, []);

  const requestPermission = async () => {
    setAudioPermission('requesting');
    setError(null);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      setAudioStream(stream);
      setAudioPermission('granted');
    } catch (err) {
      setAudioPermission('denied');
      setError('audio-interview.microphone-permission.denied.description');
    }
  };

  return {
    audioPermission,
    audioStream,
    requestPermission,
    error,
  };
};

